import type { Analysis, Sample } from "@/types";

interface WorkflowProgressProps {
  samples: Sample[];
  analyses: Analysis[];
}

interface StepInfo {
  label: string;
  count: number;
  total: number;
  color: string;
}

export default function WorkflowProgress({
  samples,
  analyses,
}: WorkflowProgressProps) {
  const totalSamples = samples.length;
  const uploadedSamples = samples.filter(
    (s) => s.status !== "registered",
  ).length;
  const completedAnalyses = analyses.filter(
    (a) => a.status === "completed",
  ).length;
  const totalAnalyses = analyses.length;

  const steps: StepInfo[] = [
    {
      label: "시료 등록",
      count: totalSamples,
      total: totalSamples,
      color: totalSamples > 0 ? "bg-blue-500" : "bg-gray-300",
    },
    {
      label: "파일 업로드",
      count: uploadedSamples,
      total: totalSamples,
      color: uploadedSamples > 0 ? "bg-cyan-500" : "bg-gray-300",
    },
    {
      label: "분석 실행",
      count: totalAnalyses,
      total: totalSamples,
      color: totalAnalyses > 0 ? "bg-yellow-500" : "bg-gray-300",
    },
    {
      label: "분석 완료",
      count: completedAnalyses,
      total: totalAnalyses || totalSamples,
      color: completedAnalyses > 0 ? "bg-green-500" : "bg-gray-300",
    },
  ];

  if (totalSamples === 0 && totalAnalyses === 0) {
    return null;
  }

  // Calculate overall progress percentage
  const maxSteps = 4;
  let completedSteps = 0;
  if (totalSamples > 0) completedSteps++;
  if (uploadedSamples > 0) completedSteps++;
  if (totalAnalyses > 0) completedSteps++;
  if (completedAnalyses > 0 && completedAnalyses === totalAnalyses)
    completedSteps++;
  const progressPercent = Math.round((completedSteps / maxSteps) * 100);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">파이프라인 진행률</h3>
        <span className="text-sm font-semibold text-gray-900">
          {progressPercent}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step indicators */}
      <div className="grid grid-cols-4 gap-2">
        {steps.map((step) => (
          <div key={step.label} className="text-center">
            <div
              className={`h-1.5 rounded-full mb-1 ${step.color}`}
            />
            <p className="text-xs font-medium text-gray-600">{step.label}</p>
            <p className="text-xs text-gray-400">
              {step.count}/{step.total}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
